// Which workspace goes in the URL.
//
// Every in-app link is built from the tenant segment of the current path
// (`/t/<segment>/app/...`, `/dashboard/<segment>/...`). That segment is supposed to be
// the workspace's slug — "demo" — but the session only ever stored the tenant's name and
// id, so the fallback took over and people saw
// `/t/64ff8bc6-3bf0-4413-88f1-1e95e197d531/app/music` in the address bar and in every
// link they copied. The id still resolves (old bookmarks keep working); it is simply
// never what we choose to show.

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export interface TenantLike {
  id?: string;
  slug?: string | null;
  subdomain?: string | null;
}

/** True when a path segment is a bare tenant id rather than a workspace name. */
export const isTenantId = (segment?: string | null): boolean => !!segment && UUID_RE.test(segment.trim());

/** The segment to put in a URL: the workspace slug, else its subdomain, else the id. */
export function tenantPathSegment(tenant?: TenantLike | null): string {
  const slug = (tenant?.slug || '').trim();
  if (slug) return slug;
  const sub = (tenant?.subdomain || '').trim();
  if (sub) return sub;
  return (tenant?.id || '').trim();
}

/** The two route families that carry a workspace segment. */
const TENANT_ROOTS = new Set(['t', 'dashboard']);

/** Rewrite only the workspace segment of a path, leaving the rest exactly as it was.
 *  Returns the path unchanged when there is nothing to swap. */
export function swapTenantSegment(pathname: string, next: string): string {
  if (!next) return pathname;
  const parts = pathname.split('/');
  // parts[0] is '' for an absolute path, so the root is parts[1] and the segment parts[2].
  if (parts.length < 3 || !TENANT_ROOTS.has(parts[1]) || !parts[2]) return pathname;
  if (parts[2] === next) return pathname;
  parts[2] = next;
  return parts.join('/');
}

/** Should the address bar be corrected? Only when it is showing a raw id and we now know
 *  the workspace's name — never to overwrite a slug someone typed. */
export function shouldHealTenantPath(currentSegment: string | undefined, tenant: TenantLike | null | undefined): string | null {
  const nice = tenantPathSegment(tenant);
  if (!nice || !currentSegment) return null;
  if (nice === currentSegment || isTenantId(nice)) return null;
  return isTenantId(currentSegment) && currentSegment === (tenant?.id || '').trim() ? nice : null;
}
